/*
 * Ring size finder of the product page.
 * Sizes come from the table rendered by the block, so the theme editor stays the single source of the numbers.
 */
if (!customElements.get('cvb-ring-finder')) {
  // Width of a bank card (ISO/IEC 7810 ID-1), the ruler used to learn how many pixels one millimetre takes on this screen
  const CARD_WIDTH_MM = 53.98;
  const STORAGE_KEY = 'cvb-ring-pixels-per-mm';

  const format = (value, decimals) => value.toFixed(decimals).replace('.', ',');

  customElements.define('cvb-ring-finder', class extends HTMLElement {
    connectedCallback() {
      if (this.isReady) return;
      this.isReady = true;

      this.details = this.querySelector('[data-cvb-details]');
      this.dialog = this.querySelector('[data-cvb-dialog]');
      this.sizes = [...this.querySelectorAll('[data-cvb-row]')].map((row) => ({
        size: row.dataset.cvbRow,
        circumference: Number(row.dataset.circumference) * 10,
        diameter: Number(row.dataset.diameter),
        row
      })).filter((entry) => entry.circumference > 0 && entry.diameter > 0);
      if (!this.sizes.length) return;

      // Sizes this product is sold in, keyed by the bare number: { "16": "T16" }
      this.variantValues = {};
      (this.dataset.variantMap || '').split('|').filter(Boolean).forEach((entry) => {
        const [size, value] = entry.split(':');
        this.variantValues[size] = value;
      });

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-cvb-mode]')) this.onModeChange();
      });

      this.setupScreen();
    }

    onClick(event) {
      const tab = event.target.closest('[data-cvb-tab]');
      if (tab) return this.showPane(tab.dataset.cvbTab);

      if (event.target.closest('[data-cvb-close]')) {
        this.details.open = false;
        return;
      }

      if (event.target.closest('[data-cvb-help]') && this.dialog && this.dialog.showModal) return this.dialog.showModal();
      if (this.dialog && (event.target === this.dialog || event.target.closest('[data-cvb-dialog-close]'))) return this.dialog.close();

      const pick = event.target.closest('[data-cvb-pick]');
      if (pick) return this.apply(pick.dataset.cvbPick, pick);

      const apply = event.target.closest('[data-cvb-apply], [data-cvb-screen-apply]');
      if (apply) return this.apply(apply.dataset.size, apply);

      const adjust = event.target.closest('[data-cvb-adjust]');
      if (adjust) return this.showRing(this.ringIndex + Number(adjust.dataset.cvbAdjust));

      if (event.target.closest('[data-cvb-next]')) return this.showStep('ring');
      if (event.target.closest('[data-cvb-back]')) return this.showStep('calibrate');
    }

    onInput(event) {
      if (event.target.matches('[data-cvb-value]')) this.readMeasure();
      if (event.target.matches('[data-cvb-range]')) this.showRing(Number(event.target.value));
    }

    showPane(name) {
      this.querySelectorAll('[data-cvb-tab]').forEach((tab) => tab.setAttribute('aria-selected', tab.dataset.cvbTab === name ? 'true' : 'false'));
      this.querySelectorAll('[data-cvb-pane]').forEach((pane) => { pane.hidden = pane.dataset.cvbPane !== name; });
      if (name === 'screen') this.fitCard(this.cardWidth);
    }

    /* Typed measure -> nearest size of the table */

    onModeChange() {
      const isDiameter = this.mode === 'diameter';
      this.querySelector('[data-cvb-field-label]').textContent = isDiameter ? 'Diâmetro interno do anel' : 'Circunferência do dedo';
      this.querySelector('[data-cvb-unit]').textContent = isDiameter ? 'mm' : 'cm';
      this.querySelector('[data-cvb-value]').placeholder = isDiameter ? 'Ex.: 17,8' : 'Ex.: 5,6';
      this.readMeasure();
    }

    get mode() {
      return this.querySelector('[data-cvb-mode]:checked').value;
    }

    readMeasure() {
      const result = this.querySelector('[data-cvb-result]');
      const value = parseFloat(this.querySelector('[data-cvb-value]').value.replace(',', '.'));

      if (!value || value <= 0) {
        result.hidden = true;
        return;
      }

      // Everything is compared as an inner circumference in millimetres.
      // A circumference above 30 was typed in mm, and a diameter below 4 in cm: both happen often.
      const circumference = this.mode === 'diameter'
        ? (value < 4 ? value * 10 : value) * Math.PI
        : (value > 30 ? value : value * 10);

      const first = this.sizes[0];
      const last = this.sizes[this.sizes.length - 1];
      const step = this.sizes.length > 1 ? (last.circumference - first.circumference) / (this.sizes.length - 1) : 1;
      const label = this.querySelector('[data-cvb-result-label]');
      const size = this.querySelector('[data-cvb-result-size]');
      const detail = this.querySelector('[data-cvb-result-detail]');
      const button = this.querySelector('[data-cvb-apply]');

      result.hidden = false;

      // Outside the table: saying so is more useful than snapping to its first or last size
      if (circumference < first.circumference - step * 0.6 || circumference > last.circumference + step * 0.6) {
        result.classList.add('is-out');
        label.textContent = 'Medida fora da tabela';
        size.textContent = '';
        detail.textContent = this.mode === 'diameter'
          ? `Os aros ${first.size} a ${last.size} vão de ${format(first.diameter, 1)} a ${format(last.diameter, 1)} mm de diâmetro interno. Confira a medida e a unidade.`
          : `Os aros ${first.size} a ${last.size} vão de ${format(first.circumference / 10, 1)} a ${format(last.circumference / 10, 1)} cm de circunferência. Confira a medida e a unidade.`;
        button.hidden = true;
        return;
      }

      const match = this.sizes.reduce((best, entry) => (Math.abs(entry.circumference - circumference) < Math.abs(best.circumference - circumference) ? entry : best));

      result.classList.remove('is-out');
      label.textContent = 'Seu tamanho CVB correspondente';
      size.textContent = `Aro ${match.size}`;
      detail.textContent = this.describe(match);
      this.offer(button, match);
      this.highlight(match.size);
    }

    describe(entry) {
      return `Diâmetro interno ${format(entry.diameter, 1)} mm · Circunferência ${format(entry.circumference / 10, 1)} cm`;
    }

    /* The button only shows for sizes this product is sold in */
    offer(button, entry) {
      const isSold = Boolean(this.variantValues[entry.size]);
      button.hidden = !isSold;
      button.dataset.size = entry.size;
      button.textContent = `Selecionar aro ${entry.size}`;
    }

    highlight(size) {
      this.sizes.forEach((entry) => entry.row.classList.toggle('is-match', entry.size === String(size)));
    }

    /* Marks the size in the variant picker of the product form, which then updates price, stock and the buy button */
    apply(size, button) {
      const value = this.variantValues[size];
      if (!value) return;

      const scope = this.closest('.product-form') || document;
      const radio = [...scope.querySelectorAll('product-variants input[type="radio"]')].find((input) => input.value === value);
      const select = [...scope.querySelectorAll('product-variants select')].find((element) => [...element.options].some((option) => option.value === value));

      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (select) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        return;
      }

      this.highlight(size);

      const original = button.textContent;
      button.textContent = `Aro ${size} selecionado`;
      button.classList.add('is-done');
      window.setTimeout(() => {
        button.textContent = original;
        button.classList.remove('is-done');
      }, 1800);
    }

    /* Measuring a ring against the screen */

    setupScreen() {
      this.card = this.querySelector('[data-cvb-card]');
      this.handle = this.querySelector('[data-cvb-handle]');
      this.ringIndex = Math.floor(this.sizes.length / 2);
      if (!this.card || !this.handle) return;

      let stored = 0;
      try { stored = Number(window.localStorage.getItem(STORAGE_KEY)) || 0; } catch (error) { /* private mode: start from the estimate */ }

      // Without a saved calibration: 96 px per inch on computers, about 6 px per mm on phones
      const estimate = window.matchMedia('(pointer: coarse)').matches ? 6 : 96 / 25.4;
      this.cardWidth = (stored > 2 && stored < 12 ? stored : estimate) * CARD_WIDTH_MM;

      let startX = 0;
      let startWidth = 0;
      let isDragging = false;

      this.handle.addEventListener('pointerdown', (event) => {
        isDragging = true;
        startX = event.clientX;
        startWidth = this.card.getBoundingClientRect().width;
        event.preventDefault();
      });

      // On the window, so the drag follows the finger even when it leaves the handle
      this.onPointerMove = (event) => { if (isDragging) this.fitCard(startWidth + event.clientX - startX); };
      this.onPointerUp = () => { isDragging = false; };
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);

      this.handle.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') this.fitCard(this.cardWidth + 1);
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') this.fitCard(this.cardWidth - 1);
        else return;
        event.preventDefault();
      });

      this.details.addEventListener('toggle', () => { if (this.details.open) this.fitCard(this.cardWidth); });
      this.showRing(this.ringIndex);
    }

    fitCard(width) {
      if (!this.card) return;

      // The stage runs to the edges of the screen on phones, where a real card takes almost its whole width
      const stage = this.card.parentElement;
      const room = stage.clientWidth - 24;
      this.cardWidth = Math.max(150, Math.min(width, room > 150 ? room : width));
      this.card.style.width = `${this.cardWidth}px`;
      this.handle.setAttribute('aria-valuenow', Math.round(this.cardWidth));
      this.handle.setAttribute('aria-valuemax', Math.round(Math.max(room, 150)));
    }

    get pixelsPerMm() {
      return this.cardWidth / CARD_WIDTH_MM;
    }

    showStep(name) {
      this.querySelectorAll('[data-cvb-step]').forEach((step) => { step.hidden = step.dataset.cvbStep !== name; });

      if (name === 'ring') {
        try { window.localStorage.setItem(STORAGE_KEY, String(this.pixelsPerMm)); } catch (error) { /* not saved: asked again next visit */ }
        this.showRing(this.ringIndex);
      } else {
        this.fitCard(this.cardWidth);
      }
    }

    showRing(index) {
      this.ringIndex = Math.max(0, Math.min(this.sizes.length - 1, index));
      const entry = this.sizes[this.ringIndex];
      const circle = this.querySelector('[data-cvb-circle]');
      const range = this.querySelector('[data-cvb-range]');
      if (!circle || !range) return;

      const diameter = entry.diameter * this.pixelsPerMm;
      circle.style.width = `${diameter}px`;
      circle.style.height = `${diameter}px`;
      range.value = this.ringIndex;
      range.setAttribute('aria-valuetext', `Aro ${entry.size}`);

      this.querySelector('[data-cvb-screen-size]').textContent = `Aro ${entry.size}`;
      this.querySelector('[data-cvb-screen-detail]').textContent = this.describe(entry);
      this.offer(this.querySelector('[data-cvb-screen-apply]'), entry);
    }

    disconnectedCallback() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }
  });
}
